import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import { ChatMessage } from '../ChatMessage';
import { ChatSticker } from '../ChatSticker';
import { ChatAudio } from '../ChatAudio';
import { ChatImage } from '../ChatImage';
import { MessageResponse } from '../../types';
import { ChatVideo } from '../ChatVideo';
import { ChatContact } from '../ChatContact';
import { ChatDocument } from '../ChatDocument';
import { ChatLocation } from '../ChatLocation';
import { ChatBase } from '../common/ChatBase';
import { CaseResponseDto, PagedResponse } from '@bandido/shared/dtos';
import { OverlayScrollbarsComponent, OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';
import { useMemo, useRef } from 'react';
import { useScrollHandling } from '@frontend/features/common/hooks/useScrollHandling';
import { useIntersectionObserver } from '@frontend/features/common/hooks/useIntersectionObserver';
import { useWhatsappClient } from '../WhatsappClientContext';
import { useOnResize } from '@frontend/features/cases/components/CaseContentHistory/hooks/useOnResize';
import { LoadingIndicator } from '@frontend/features/cases/components/CaseContentHistory/styled';

interface Props {
  casesData?: PagedResponse<CaseResponseDto>;
  currentCaseId?: number;
}

const getDateLabel = (dateString: string): string => {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const isSameDay = (d1: Date, d2: Date) =>
    d1.getDate() === d2.getDate() && d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear();

  if (isSameDay(date, today)) return 'Hoy';
  if (isSameDay(date, yesterday)) return 'Ayer';

  return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const ChatContainer = ({ casesData, currentCaseId }: Props) => {
  const theme = useTheme();
  const { messages, fetchNextPage, isFetchingNextPage, hasNextPage } = useWhatsappClient();

  const sortedMessages = [...messages].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  const grouped = sortedMessages.reduce(
    (acc, msg) => {
      const label = getDateLabel(msg.createdAt);
      acc[label] = acc[label] || [];
      acc[label].push(msg);
      return acc;
    },
    {} as Record<string, MessageResponse[]>,
  );

  const scrollRef = useRef<OverlayScrollbarsComponentRef<'div'>>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef(null);

  const { shouldAutoScroll, scrollDirection, handleScroll } = useScrollHandling();

  const scrollInstance = useMemo(() => scrollRef.current?.osInstance()?.elements()?.viewport, [scrollRef.current]);

  useIntersectionObserver({
    targetRef: topRef,
    onIntersect: () => {
      fetchNextPage();
      scrollInstance?.scrollTo({ top: 250 });
    },
    enabled: !isFetchingNextPage && scrollDirection === 'up' && hasNextPage,
  });

  useOnResize(contentRef, () => {
    if (!scrollInstance || scrollDirection === 'up' || !shouldAutoScroll) return;
    const maxScrollHeight = scrollInstance.scrollHeight - scrollInstance.clientHeight;
    scrollInstance.scrollTop = maxScrollHeight;
  });

  const returnItem = (msg: MessageResponse) => {
    const getUserName = (msg: MessageResponse) => {
      if (msg.inbound !== false || (!msg.createdByFirstName && !msg.createdByLastName)) {
        return null;
      }
      return `${msg.createdByFirstName || ''} ${msg.createdByLastName || ''}`.trim();
    };

    const userName = getUserName(msg);

    const messageComponent = (() => {
      switch (msg.type) {
        case 'text':
          return (
            <ChatBase key={msg.id} {...{...msg, casesData, currentCaseId}}>
              <ChatMessage message={msg.text || ''} />
            </ChatBase>
          );

        case 'contacts':
          return (
            <ChatBase key={msg.id} {...{...msg, casesData, currentCaseId}}>
              <ChatContact message={msg.text || ''} />
            </ChatBase>
          );

        case 'template':
          if (msg.caption) {
            switch (msg.caption) {
              case 'image':
                return (
                  msg.text && (
                    <ChatBase key={msg.id} {...{...msg, casesData, currentCaseId}}>
                      <ChatImage url={msg.text} />
                    </ChatBase>
                  )
                );
              case 'video':
                return (
                  msg.text && (
                    <ChatBase key={msg.id} {...{...msg, casesData, currentCaseId}}>
                      <ChatVideo fileId={msg.text} />
                    </ChatBase>
                  )
                );
              case 'audio':
                return (
                  msg.text && (
                    <ChatBase
                      key={msg.id}
                      {...{...msg, casesData, currentCaseId}}
                      backgroundColor={theme.palette.primary.main}
                      textColor="common.white"
                    >
                      <ChatAudio fileId={msg.text} />
                    </ChatBase>
                  )
                );
              case 'document':
                return (
                  msg.text && (
                    <ChatBase
                      key={msg.id}
                      {...{...msg, casesData, currentCaseId}}
                      backgroundColor={theme.palette.primary.main}
                      textColor="common.white"
                    >
                      <ChatDocument fileId={msg.text} filename={msg.filename as string} inbound={msg.inbound} />
                    </ChatBase>
                  )
                );
              default:
                return (
                  <ChatBase key={msg.id} {...{...msg, casesData, currentCaseId}}>
                    <ChatMessage message={msg.text || ''} />
                  </ChatBase>
                );
            }
          } else {
            return (
              <ChatBase key={msg.id} {...{...msg, casesData, currentCaseId}}>
                <ChatMessage message={msg.text || ''} />
              </ChatBase>
            );
          }

        case 'sticker':
          return (
            msg.text && (
            <ChatBase key={msg.id} {...{...msg, casesData, currentCaseId}}>
              <ChatSticker url={msg.text} />
            </ChatBase>
          )
        );

        case 'audio':
          return (
            msg.text && (
            <ChatBase
              key={msg.id}
              {...{...msg, casesData, currentCaseId}}
              backgroundColor={theme.palette.primary.main}
              textColor="common.white"
            >
                <ChatAudio fileId={msg.text} />
              </ChatBase>
            )
          );

        case 'video':
          return (
            msg.text && (
            <ChatBase key={msg.id} {...{...msg, casesData, currentCaseId}}>
              <ChatVideo fileId={msg.text} />
            </ChatBase>
          )
        );

        case 'image':
          return (
            msg.text && (
            <ChatBase key={msg.id} {...{...msg, casesData, currentCaseId}}>
              <ChatImage url={msg.text} caption={msg.caption || undefined} />
            </ChatBase>
          )
        );

        case 'document':
          return (
            msg.text && (
            <ChatBase
              key={msg.id}
              {...{...msg, casesData, currentCaseId}}
              backgroundColor={theme.palette.primary.main}
              textColor="common.white"
            >
                <ChatDocument
                  fileId={msg.text}
                  caption={msg.caption || undefined}
                  filename={msg.filename as string}
                  inbound={msg.inbound}
                />
              </ChatBase>
            )
          );

        case 'location':
          return (
            msg.text && (
              <ChatBase key={msg.id} {...msg} currentCaseId={currentCaseId}>
                <ChatLocation latLong={msg.text} />
              </ChatBase>
            )
          );

        default:
          return null;
      }
    })();

    return (
      <Box key={msg.id}>
        {userName && (
          <Box display="flex" justifyContent={msg.inbound ? 'flex-start' : 'flex-end'} mb={0.5}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 500,
                ml: msg.inbound ? 1 : 0,
                mr: msg.inbound ? 0 : 1,
              }}
            >
              {userName}
            </Typography>
          </Box>
        )}
        {messageComponent}
      </Box>
    );
  };

  return (
    <OverlayScrollbarsComponent
      ref={scrollRef}
      events={{ scroll: handleScroll }}
      options={{
        scrollbars: {
          autoHide: 'leave',
          theme: 'os-theme-light',
        },
      }}
      style={{
        overflow: 'hidden',
        height: '100%',
        padding: theme.spacing(0, 3),
      }}
    >
      <Box p={2} flexDirection="column" display="flex" justifyContent="end" ref={contentRef}>
        <Box sx={{ height: 50, width: '100%' }} ref={topRef} />
        {isFetchingNextPage && (
          <LoadingIndicator>
            <CircularProgress size={24} />
          </LoadingIndicator>
        )}
        {Object.entries(grouped).map(([label, msgs]) => (
          <Box key={label} mb={4}>
            <Box display="flex" justifyContent="center" sx={{ mb: 2 }}>
              <Box borderRadius={1} border={1} borderColor="divider" bgcolor="grey.200" py={0.5} px={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  {label}
                </Typography>
              </Box>
            </Box>
            {msgs.map((msg) => returnItem(msg))}
          </Box>
        ))}
      </Box>
    </OverlayScrollbarsComponent>
  );
};
